import {
  AccountId,
  ContractId,
  TokenId,
  TransactionId, 
} from "@hashgraph/sdk";
import { Stack, Box } from "@mui/system"; 
import { ContractFunctionParameterBuilder } from "../services/wallets/contractFunctionParameterBuilder";
import { useWalletInterface } from "../services/wallets/useWalletInterface";
import { useEffect, useState } from "react";
import { Button, Typography, CircularProgress } from "@mui/material"; 
import SendIcon from '@mui/icons-material/Send';
import AddLinkIcon from '@mui/icons-material/AddLink'; 
import ReplayIcon from '@mui/icons-material/Replay'; 
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { isAssociatedWithToken } from "../services/mirrorUtils";

type MintStatus = "IDLE" | "WALLET_APPROVE" | "FINDING_NUMBER" | "SUCCESS" | "ERROR";

export default function Home() {
  const { walletInterface, accountId } = useWalletInterface();
  
  const [mintStatus, setMintStatus] = useState<MintStatus>("IDLE");
  const [mintResult, setMintResult] = useState<{serial: number, metadata: string} | null>(null);
  const [errorMessage, setErrorMessage] = useState(""); 
  
  const [isAssociated, setIsAssociated] = useState(false);
  const [checkingAssociation, setCheckingAssociation] = useState(false);
  const [associating, setAssociating] = useState(false);
  const [associateError, setAssociateError] = useState("");

  const tokenId = "0.0.10215492"; 
  const contractId = "0.0.10215461"; 

  const getMonthData = (metadata: string) => {
    if (metadata.includes("10214963")) return { name: "January", image: "january.png" };
    if (metadata.includes("10214940")) return { name: "February", image: "february.png" };
    if (metadata.includes("10214992")) return { name: "March", image: "march.png" };
    if (metadata.includes("10214902")) return { name: "April", image: "april.png" };
    if (metadata.includes("10215003")) return { name: "May", image: "may.png" };
    if (metadata.includes("10214982")) return { name: "June", image: "june.png" };
    if (metadata.includes("10214973")) return { name: "July", image: "july.png" };
    if (metadata.includes("10214914")) return { name: "August", image: "august.png" };
    if (metadata.includes("10215026")) return { name: "September", image: "september.png" };
    if (metadata.includes("10215017")) return { name: "October", image: "october.png" };
    if (metadata.includes("10215009")) return { name: "November", image: "november.png" };
    if (metadata.includes("10214932")) return { name: "December", image: "december.png" };
    
    return { name: "2026 Special", image: "january.png" };
  };

  const checkMirrorNodeForYield = async (txId: TransactionId): Promise<{ status: "PENDING" | "REVERTED" | "SUCCESS", serial: number | null, metadata: string | null }> => {
    try {
      const txIdString = txId.toString();
      const [accountPart, timePart] = txIdString.split('@');
      const [seconds, nanos] = timePart.split('.');
      const formattedTxId = `${accountPart}-${seconds}-${nanos}`;
      
      const url = `https://mainnet.mirrornode.hedera.com/api/v1/contracts/results/${formattedTxId}`;
      const response = await fetch(url);
      if (!response.ok) return { status: "PENDING", serial: null, metadata: null };
      
      const data = await response.json();
      if (data.result !== "SUCCESS") return { status: "REVERTED", serial: null, metadata: null };

      if (data.logs && data.logs.length > 0) {
         const targetLog = data.logs.find((log: any) => log.data && log.data.length > 130);
         if (targetLog) {
            const rawData = targetLog.data.replace("0x", "");
            const serialHex = rawData.substring(0, 64);
            const serial = parseInt(serialHex, 16);
            const lengthHex = rawData.substring(128, 192);
            const length = parseInt(lengthHex, 16) * 2; 
            const metadataHex = rawData.substring(192, 192 + length);
            
            let metadataStr = "";
            for (let n = 0; n < metadataHex.length; n += 2) {
                metadataStr += String.fromCharCode(parseInt(metadataHex.substring(n, n + 2), 16));
            }
            return { status: "SUCCESS", serial: serial, metadata: metadataStr };
         }
      }
      return { status: "SUCCESS", serial: null, metadata: null };
    } catch (e) {
      return { status: "PENDING", serial: null, metadata: null };
    }
  };

  useEffect(() => {
    const checkAssociation = async () => {
      if (!accountId) return;
      setCheckingAssociation(true);
      try {
        const associated = await isAssociatedWithToken(accountId, tokenId);
        setIsAssociated(associated);
      } catch (error) {
        console.error("Error checking association:", error);
      } finally {
        setCheckingAssociation(false);
      }
    };
    checkAssociation();
  }, [accountId, tokenId]);

  const handleAssociate = async () => {
    if (!walletInterface) return;
    setAssociating(true);
    setAssociateError(""); 
    try {
      await walletInterface.associateToken(TokenId.fromString(tokenId));
      
      // Fixing the "sticky button" by polling the Mirror Node
      let isActuallyAssociated = false;
      for (let i = 0; i < 5; i++) {
        await new Promise(r => setTimeout(r, 3000)); 
        const check = await isAssociatedWithToken(accountId, tokenId);
        if (check) {
          isActuallyAssociated = true;
          break;
        }
      }
      setIsAssociated(isActuallyAssociated);
    } catch (err: any) {
      setAssociateError(err.message || "Association failed");
    } finally {
      setAssociating(false);
    }
  };

  const handleCallContract = async () => {
    if (!walletInterface) return;
    setMintStatus("WALLET_APPROVE");
    setMintResult(null); 
    setErrorMessage("");
    const params = new ContractFunctionParameterBuilder();
    try {
      const txId = await walletInterface.executeContractFunction(
        ContractId.fromString(contractId),
        "mint", 
        params,
        400_000
      );
      if (txId) {
        setMintStatus("FINDING_NUMBER");
        let finalOutcome = false;
        for (let i = 0; i < 20; i++) { 
            const result = await checkMirrorNodeForYield(txId);
            if (result.status === "REVERTED") {
                setErrorMessage("Error: Minting for 2026 has ended or not started.");
                setMintStatus("ERROR");
                finalOutcome = true;
                break;
            }
            if (result.status === "SUCCESS" && result.serial !== null && result.metadata !== null) {
                setMintResult({ serial: result.serial, metadata: result.metadata });
                setMintStatus("SUCCESS");
                finalOutcome = true;
                break;
            }
            await new Promise(r => setTimeout(r, 1500)); 
        }
        if (!finalOutcome) {
            setErrorMessage("Mirror node timeout. Check your wallet history.");
            setMintStatus("ERROR");
        }
      } else {
        setMintStatus("IDLE");
      }
    } catch (err) {
      setErrorMessage("Transaction cancelled or failed.");
      setMintStatus("ERROR"); 
    } 
  };

  return (
    <Stack alignItems="center" spacing={4} p={4} maxWidth={600} mx="auto">
      <Typography variant="h4" color="white" align="center" fontWeight="bold">
        TOZM Year 2026
      </Typography>

      {!accountId ? (
        <Typography color="white">Connect your wallet to begin.</Typography>
      ) : (
        <>
            {mintStatus === "SUCCESS" && mintResult && (
                <Stack alignItems="center" spacing={2}>
                  {(() => {
                    const monthData = getMonthData(mintResult.metadata);
                    return (
                        <>
                            <Typography color="white" variant="h5" align="center">
                                You minted a {monthData.name} NFT!
                            </Typography>
                            <Box 
                                component="img"
                                src={`/${monthData.image}`} 
                                alt={monthData.name}
                                sx={{
                                    width: 400, height: 400, objectFit: 'contain', borderRadius: 4,
                                    boxShadow: '0px 0px 30px rgba(144, 202, 249, 0.3)', bgcolor: 'rgba(0,0,0,0.5)'
                                }}
                            />
                            <Typography color="#90caf9" variant="h6">Serial #{mintResult.serial}</Typography>
                        </>
                    );
                  })()}
                  <Button variant='contained' startIcon={<ReplayIcon />} onClick={handleCallContract}>Mint another!</Button>
                </Stack>
            )}

            {mintStatus === "ERROR" && (
               <Stack direction="column" alignItems="center" spacing={2} sx={{ border: '1px solid #f44336', p: 3, borderRadius: 2, bgcolor: 'rgba(244, 67, 54, 0.1)' }}>
                  <ErrorOutlineIcon color="error" sx={{ fontSize: 40 }}/>
                  <Typography color="error" variant="body1" align="center">{errorMessage}</Typography>
                  <Button variant='contained' onClick={handleCallContract}>Try Again</Button>
               </Stack>
            )}

            {(mintStatus === "IDLE" || mintStatus === "WALLET_APPROVE" || mintStatus === "FINDING_NUMBER") && (
              <Stack direction="column" gap={2} alignItems="center" width="100%">
                {checkingAssociation ? (
                   <CircularProgress size={24} color="inherit" />
                ) : !isAssociated ? (
                   <>
                      <Typography color="warning.main" align="center">You must be associated with {tokenId} to mint Tyear NFTs.</Typography>
                      <Button variant="contained" color="secondary" onClick={handleAssociate} disabled={associating} startIcon={associating ? <CircularProgress size={20} color="inherit" /> : <AddLinkIcon />}>
                        Associate Token
                      </Button>
                      {associateError && <Typography variant="caption" color="error">{associateError}</Typography>}
                   </>
                ) : (
                   <>
                      {mintStatus === "IDLE" && (
                        <Stack direction="column" gap={3} width="100%">
                            <Typography color="white" align="center">
                                Minting is free, except each NFT costs about $0.03 in network fees. You can mint as many as you want. Contract ID is {contractId}.
                            </Typography>
                            <Button variant="contained" size="large" onClick={handleCallContract} startIcon={<SendIcon />} fullWidth>Mint Current Month NFT</Button>
                        </Stack>
                      )}
                      {mintStatus === "WALLET_APPROVE" && <Typography color="white">Check your wallet...</Typography>}
                      {mintStatus === "FINDING_NUMBER" && <Stack alignItems="center" spacing={2}><CircularProgress size={30} /><Typography color="white">Minting your 2026 NFT...</Typography></Stack>}
                   </>
                )}
              </Stack>
            )}
        </>
      )}

      {/* RESTORED FOOTER SECTION */}
      <Typography color="white" align="center">
        Need help? See the{" "}
        <a href="https://tozm.io/?month13" target="_blank" rel="noopener noreferrer" style={{ color: "#90caf9" }}>
          TOZM Year 2026 info page
        </a>
        {", "}
        <a href="https://discord.gg/SN8zxugwgg" target="_blank" rel="noopener noreferrer" style={{ color: "#90caf9" }}>
          Discord
        </a>
        {", or "}
        <a href="https://x.com/tozmdotio" target="_blank" rel="noopener noreferrer" style={{ color: "#90caf9" }}>
          X
        </a>.
        <br /><br />
        By using this dApp you signify your agreement to our{" "}
        <a href="https://tozm.io/?page=terms" target="_blank" rel="noopener noreferrer" style={{ color: "#90caf9" }}>
          Terms
        </a>.
      </Typography>
    </Stack>
  );
}